/**
 * コーディングテストの練習の 1 問の頁の、左に問題、右にエディターの 2 列。
 *
 * 作りはコーディング面接問題の頁 (CodingTryPanel) と同じ部品 (CodingLessonPage、CodingLessonFooter) を使う。
 * CodingTryPanel は関数を呼ぶ形に縛られているので、標準入出力の兄弟として別に作った。
 * CodingTryPanel を書き換えないのは、いま PR #3583 (結果の札) と PR #3584 (大きな入力で測る) が書き換えているため。
 * 高さ合わせや狭い幅の判定などの小さな部品はここに写してある。両方が入ったら、共通の hooks に切り出す。
 *
 * 右の下の欄は「入力」と「出力」の 2 つ。例を選ぶ札 (例 1、例 2、自分の入力) で入力を替えて「実行する」を押すと、
 * 手元のブラウザの Worker で 1 回走らせる。「提出して採点」は隠れたテストを取りに行って全部走らせ、結果を釦の上に残す。
 * 実行はブラウザの Worker の中だけで、サーバーには送らない (時間を越えたら Worker を止める)。
 * 書いたコードと提出の結果は、手元のブラウザ (localStorage) だけに残す。
 */
import { type ReactNode, type RefObject, Suspense, useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Button } from "@noiman/ui/button";
import { lazyNamed } from "@noiman/ui/lazy-named";
import { useNearViewport } from "@noiman/ui/use-near-viewport";
import { Textarea } from "@noiman/ui/textarea";
import { CodingLessonPage } from "@noiman/ui/coding-lesson-page";
import { CodingLessonFooter } from "@noiman/ui/coding-lesson-footer";
import { Link, useNavigate } from "react-router";
import { StdioSubmitResult } from "@/components/coding-test/StdioSubmitResult";
import { useEditorPrefs } from "@/components/lessons/useEditorPrefs";
import { phraseText } from "@/lib/coding-interview";
import { type CodingTestExample, CODING_TEST_LANGUAGES } from "@/lib/coding-test";
import { readAllProgress, readDraft, saveProgress, writeDraft } from "@/lib/coding-test-progress";
import { notifyProgressChanged } from "@/lib/problem-progress";
import { judgeNote, stdioCaseViews } from "@/lib/coding-test-result";
import { compareOutput } from "@/lib/stdio-judge/compare";
import type { StdioJudge } from "@/lib/stdio-judge/judge";
import type { CompareMode, HiddenTest, JudgeLanguage, JudgeReport, RunReport } from "@/lib/stdio-judge/types";

const CodeEditor = lazyNamed(
  () => import("@noiman/ui/code-editor"),
  (mod) => mod.CodeEditor,
);

const EXTENSION: Record<JudgeLanguage, string> = { python: "py", javascript: "js" };
const FILE_ID = "file-1";
const LANGUAGES = CODING_TEST_LANGUAGES.map((l) => l.id);
const WRAP = "[word-break:keep-all] [overflow-wrap:anywhere]";
const VIM_KEY = "coding-editor-vim-mode";

/* 触る画面で Monaco が右下に出すキーボードの印は、狭い幅でコードの上に重なって字を隠す。この欄だけ消す */
const HIDE_KEYBOARD_GLYPH = "[&_.iPadShowKeyboard]:!hidden";

const NARROW = "(max-width: 599px)";
const WIDE = "(min-width: 768px)";

function subscribeNarrow(onChange: () => void) {
  const query = window.matchMedia(NARROW);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function useNarrow() {
  return useSyncExternalStore(
    subscribeNarrow,
    () => window.matchMedia(NARROW).matches,
    () => false,
  );
}

/* 暗い版は html の data-theme が決める */
function subscribeTheme(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}

function useIsDark() {
  return useSyncExternalStore(
    subscribeTheme,
    () => document.documentElement.getAttribute("data-theme") === "dark",
    () => false,
  );
}

/*
 * 器の高さを「画面の下端まで」にする (CodingTryPanel の useFitHeight と同じ)。
 * 器の上端が頁の頭の下に来るまでは、送った分だけ伸ばす
 */
function useFitHeight(ref: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const box = ref.current;
    if (!box) return;
    const wide = window.matchMedia(WIDE);
    let frame = 0;
    const fit = () => {
      frame = 0;
      if (!wide.matches) {
        box.style.height = "";
        return;
      }
      const boxTop = Math.max(0, box.getBoundingClientRect().top);
      box.style.height = `${Math.max(200, Math.round(window.innerHeight - boxTop))}px`;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(fit);
    };
    const onWheel = (event: WheelEvent) => {
      if (!wide.matches || event.deltaY <= 0 || event.ctrlKey) return;
      const gap = box.getBoundingClientRect().top;
      if (gap <= 1) return;
      event.preventDefault();
      window.scrollBy({ top: Math.min(gap, event.deltaY) });
    };
    fit();
    if (wide.matches && window.scrollY === 0 && !window.location.hash) {
      const gap = box.getBoundingClientRect().top;
      if (gap > 1) window.scrollTo({ top: Math.round(gap) });
    }
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    wide.addEventListener("change", schedule);
    box.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      wide.removeEventListener("change", schedule);
      box.removeEventListener("wheel", onWheel);
    };
  }, [ref]);
}

type ArticleTab = "problem" | "answer";
const ARTICLE_TABS: { id: ArticleTab; label: string }[] = [
  { id: "problem", label: "問題" },
  { id: "answer", label: "解答" },
];

/** 入力の欄で選んでいる物。number は例の番号 (0 から)、"custom" は自分の入力 */
type InputChoice = number | "custom";

type RunView = { choice: InputChoice; report: RunReport } | null;

type Props = {
  slug: string;
  timeLimitMs: number;
  compare: CompareMode;
  examples: CodingTestExample[];
  starters: Record<JudgeLanguage, string>;
  /** 提出で隠れたテストを取りに行く。頁の読み込みには載せない (coding-test-hidden.client.ts) */
  loadTests: () => Promise<HiddenTest[]>;
  /** 左の列 (スマホでは上) の札ごとの中身 */
  tabs: Record<ArticleTab, ReactNode>;
  /** スマホで書く途中に「課題を見る」で開く板の中身 (問題と例) */
  task: ReactNode;
  nav: { indexHref: string; prev: { href: string; title: string } | null; next: { href: string; title: string } | null };
  crumbs: { label: string; href?: string }[];
  menu: { title: string; content: ReactNode };
  /** 左の問題の並び (ProblemSidebar)。1024px 未満では場所を取らず、下から出る棚になる */
  sidebar?: ReactNode;
  /**
   * 模擬試験の頁 (mock.page.tsx) が使うときの差分。渡すと、解答の札を出さず、結果を練習の記録 (済みの印) に残さず、
   * 下の「問題の移動」の帯を footer に替える。headerAction は頭の帯 (CodingLessonHeader) の右側に差し込む問 1、問 2 の札と時計、
   * locked は時間切れのあとで実行も提出も止める。札の列は出さない (札は問題の 1 枚だけなので)
   */
  mock?: {
    headerAction: ReactNode;
    footer: ReactNode;
    locked: boolean;
    onSubmitted: (result: { passed: number; total: number; language: JudgeLanguage; at: number }) => void;
  };
};

export function StdioTryPanel({ slug, timeLimitMs, compare, examples, starters, loadTests, tabs, task, nav, crumbs, menu, sidebar, mock }: Props) {
  const [lang, setLang] = useState<JudgeLanguage>("python");
  const [tab, setTab] = useState<ArticleTab>("problem");
  const [codes, setCodes] = useState(starters);
  const [choice, setChoice] = useState<InputChoice>(0);
  const [custom, setCustom] = useState("");
  const [runView, setRunView] = useState<RunView>(null);
  const [report, setReport] = useState<JudgeReport | null>(null);
  const [running, setRunning] = useState<"run" | "submit" | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [answerOpen, setAnswerOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [near, nearRef] = useNearViewport({ rootMargin: "400px 0px" });
  const { fontSize, setFontSize } = useEditorPrefs();
  const isDark = useIsDark();
  const narrow = useNarrow();
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement>(null);
  const judgeRef = useRef<StdioJudge | null>(null);
  useFitHeight(boxRef);

  /* SSR の頁なので、残した物は描いたあとに読む */
  useEffect(() => {
    setCodes({
      python: readDraft(slug, "python") ?? starters.python,
      javascript: readDraft(slug, "javascript") ?? starters.javascript,
    });
    if (!mock && readAllProgress()[slug]?.solved) setAnswerOpen(true);
    // slug が変わったときだけ読み直す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  /*
   * 頁を離れるときは、走っている物を止めて Worker を捨てる。
   * 採点の部品は押したときに読み込むので、読み込みの途中で離れた場合は、読み込み終わりに alive を見て作らない (codex の指摘)
   */
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      judgeRef.current?.dispose();
      judgeRef.current = null;
    };
  }, []);

  const [vim, setVim] = useState(false);
  useEffect(() => {
    try {
      setVim(localStorage.getItem(VIM_KEY) === "true");
    } catch {
      /* 記録が読めない環境では切ったまま */
    }
  }, []);

  const change = useCallback(
    (value: string) => {
      setCodes((current) => ({ ...current, [lang]: value }));
      writeDraft(slug, lang, value);
    },
    [lang, slug],
  );

  const reset = () => {
    setCodes((current) => ({ ...current, [lang]: starters[lang] }));
    setRunView(null);
    setReport(null);
    writeDraft(slug, lang, null);
  };

  /* PC で器が頭の下に着く前は狭いので、釦を押したときに頁を送って着ける */
  const dock = () => {
    const box = boxRef.current;
    if (!box || !window.matchMedia(WIDE).matches) return;
    const gap = box.getBoundingClientRect().top;
    if (Math.abs(gap) > 1) window.scrollTo({ top: Math.round(window.scrollY + gap) });
  };

  const getJudge = async (): Promise<StdioJudge> => {
    if (!judgeRef.current) {
      const { createStdioJudge } = await import("@/lib/stdio-judge");
      if (!aliveRef.current) throw new Error("頁を離れたので、採点を始めませんでした");
      judgeRef.current = createStdioJudge();
    }
    return judgeRef.current;
  };

  const stdin = choice === "custom" ? custom : examples[choice].input;

  const execute = async () => {
    dock();
    setTouched(true);
    setRunning("run");
    setProgress(lang === "python" ? "Python の準備をしています。初めてのときは数秒かかります" : null);
    try {
      const judge = await getJudge();
      const result = await judge.run({ language: lang, code: codes[lang], stdin, timeLimitMs });
      setRunView({ choice, report: result });
    } catch (error) {
      setRunView({ choice, report: { status: "unavailable", message: error instanceof Error ? error.message : String(error) } });
    }
    setRunning(null);
    setProgress(null);
  };

  const submit = async () => {
    dock();
    setTouched(true);
    setRunning("submit");
    setReport(null);
    setProgress(lang === "python" ? "Python の準備をしています。初めてのときは数秒かかります" : "テストを読み込んでいます");
    let result: JudgeReport;
    try {
      const [judge, tests] = await Promise.all([getJudge(), loadTests()]);
      result = await judge.judge({
        language: lang,
        code: codes[lang],
        tests,
        timeLimitMs,
        compare,
        onProgress: (done, total) => setProgress(`${done} / ${total} 件を採点しています`),
      });
    } catch (error) {
      result = { status: "unavailable", message: error instanceof Error ? error.message : String(error) };
    }
    setReport(result);
    if (result.status === "judged") {
      if (mock) {
        mock.onSubmitted({ passed: result.passed, total: result.total, language: lang, at: Date.now() });
      } else {
        saveProgress(slug, { passed: result.passed, total: result.total, language: lang, at: Date.now() });
        /* 同じ頁の問題の並び (ProblemSidebar) の済みの丸を、すぐ付け替える */
        notifyProgressChanged();
        if (result.passed === result.total && result.total > 0) setAnswerOpen(true);
      }
    }
    setRunning(null);
    setProgress(null);
  };

  const code = codes[lang];
  const engaged = near || touched;
  const judged = report?.status === "judged" ? report : null;
  const views = judged ? stdioCaseViews(judged, timeLimitMs) : null;
  const resultState = running === "submit" ? "checking" : judged ? "judged" : "idle";

  return (
    <div ref={boxRef} className={`flex flex-col md:h-dvh ${HIDE_KEYBOARD_GLYPH}`} data-coding-try data-stdio-try>
      <CodingLessonPage
        className="md:!h-auto md:min-h-0 md:flex-1"
        showHeaderSettings={false}
        LinkComponent={Link}
        breadcrumbs={crumbs}
        headerMenu={menu}
        headerMenuLabel="練習問題の一覧を開く"
        headerAction={mock?.headerAction}
        leftSidebar={sidebar}
        mobilePageScroll
        resumeTabs={mock ? [] : ARTICLE_TABS}
        largeResumeTabs
        resumeTabsVariant="flush"
        defaultActiveResumeTab="problem"
        onResumeTabChange={(next) => setTab(next as ArticleTab)}
        resumeSections={[
          {
            type: "node",
            content: (
              <>
                {(mock ? ARTICLE_TABS.filter((tab) => tab.id === "problem") : ARTICLE_TABS).map(({ id }) => (
                  <div key={id} hidden={tab !== id} data-coding-tab={id}>
                    {id === "answer" ? (
                      <AnswerGate open={answerOpen} onOpen={() => setAnswerOpen(true)}>
                        {tabs.answer}
                      </AnswerGate>
                    ) : (
                      tabs[id]
                    )}
                  </div>
                ))}
              </>
            ),
          },
        ]}
        chatMessages={[]}
        showChatArea={false}
        showEditorArea
        stackEditorOnMobile
        mobileSingleScroll
        mobileTaskContent={task}
        showEditorActions
        showFileSidebar={false}
        showSaveAction={false}
        runActionLabel="実行する"
        submitActionLabel="提出して採点"
        submittingLabel="採点しています"
        fileTree={[{ id: FILE_ID, name: `main.${EXTENSION[lang]}`, type: "file" }]}
        openFiles={[FILE_ID]}
        activeFileId={FILE_ID}
        availableLanguages={LANGUAGES}
        selectedLanguage={lang}
        onLanguageChange={(next) => setLang(next as JudgeLanguage)}
        fontSize={fontSize}
        onFontSizeChange={setFontSize}
        onRunCode={execute}
        onSubmitCode={submit}
        onResetCode={reset}
        isRunning={running === "run"}
        isSubmitting={running === "submit"}
        actionsDisabled={running !== null || Boolean(mock?.locked)}
        executionFailed={Boolean(runView && runView.report.status === "ran" && runView.report.exitCode !== 0)}
        editorContent={
          <div
            ref={nearRef}
            className="flex h-full min-h-[340px] flex-col"
            onPointerDown={() => setTouched(true)}
            onFocusCapture={() => {
              setTouched(true);
              dock();
            }}
            data-coding-try-editor={engaged ? "active" : "waiting"}
          >
            <div className="min-h-[160px] flex-1">
              {engaged ? (
                <Suspense fallback={<CodePreview code={code} />}>
                  <CodeEditor
                    key={`${lang}-vim-${vim}`}
                    value={code}
                    language={lang}
                    onChange={change}
                    darkMode={isDark}
                    fontSize={fontSize}
                    wordWrap={narrow ? "on" : "off"}
                    vimMode={vim}
                  />
                </Suspense>
              ) : (
                <CodePreview code={code} />
              )}
            </div>
            <IoPanel
              examples={examples}
              choice={choice}
              onChoice={setChoice}
              custom={custom}
              onCustom={setCustom}
              runView={runView}
              compare={compare}
              running={running === "run" ? progress ?? "実行しています" : null}
              timeLimitMs={timeLimitMs}
            />
          </div>
        }
        submitResult={
          report?.status === "unavailable" ? (
            <div role="status" data-stdio-result="unavailable" className={`rounded-xl bg-second p-4 text-[17px] text-main ${WRAP}`}>
              <p className="m-0 font-bold">{phraseText(report.message)}</p>
              <p className="m-0 mt-2">{phraseText("書いたコードはこの端末に残っています。Python と JavaScript は、この頁の中だけで動かします。")}</p>
            </div>
          ) : (
            <StdioSubmitResult
              state={resultState}
              progress={progress}
              views={views}
              passLine={mock ? "全部のテストに通りました。この問題は通過です" : "全部のテストに通りました。解答タブが見られるようになりました"}
              fixLine="通っていないテストを直してから、もう一度提出してください"
              idleHint="「提出して採点」を押すと、隠れたテストを全部走らせて、通ったかをここに出します"
              note={judged ? judgeNote(judged, timeLimitMs) : null}
              resetKey={report}
            />
          )
        }
      />
      {mock ? (
        mock.footer
      ) : (
        <nav aria-label="問題の移動" className="shrink-0">
          <CodingLessonFooter
            LinkComponent={Link}
            homeHref={nav.indexHref}
            homeActionLabel="練習の入口へ"
            prevLessonTitle={nav.prev?.title}
            prevLessonDisabled={!nav.prev}
            prevActionLabel="前の問題へ"
            onPrevLessonClick={nav.prev ? () => navigate(nav.prev!.href) : undefined}
            nextHref={nav.next?.href ?? nav.indexHref}
            nextLessonTitle={nav.next?.title ?? "練習の入口"}
            nextActionLabel={nav.next ? "次の問題へ" : "練習の入口へ"}
            secondaryButtonVariant="outline"
          />
        </nav>
      )}
    </div>
  );
}

/* 全部通るまで解答は閉じておく。詰まったら自分で開ける (HTML には入っているので、検索と AI のクローラーは読める) */
function AnswerGate({ open, onOpen, children }: { open: boolean; onOpen: () => void; children: ReactNode }) {
  if (open) return <>{children}</>;
  return (
    <div data-stdio-answer-gate className={`flex flex-col items-start gap-4 rounded-xl border border-main bg-surface p-5 ${WRAP}`}>
      <p className="m-0 text-[17px] font-bold leading-[1.8] text-main">{phraseText("全部のテストに通ると、解答と考え方が開きます。")}</p>
      <p className="m-0 text-[17px] leading-[1.8] text-main">
        {phraseText("先にヒントを試すと、自分で解けた手応えが残ります。どうしても詰まったときは、ここから開けます。")}
      </p>
      <Button variant="outline" size="lg" onClick={onOpen} data-stdio-answer-open>
        解答を開く
      </Button>
      <div hidden>{children}</div>
    </div>
  );
}

function CodePreview({ code }: { code: string }) {
  return (
    <pre
      tabIndex={0}
      className="m-0 h-full overflow-auto bg-surface p-4 font-mono [tab-size:2] [@media(max-width:599px)]:whitespace-pre-wrap text-[15px] leading-[22px] text-main"
    >
      <code>{code}</code>
    </pre>
  );
}

/* ---- 入力と出力の欄 ---- */

function IoPanel({
  examples,
  choice,
  onChoice,
  custom,
  onCustom,
  runView,
  compare,
  running,
  timeLimitMs,
}: {
  examples: CodingTestExample[];
  choice: InputChoice;
  onChoice: (next: InputChoice) => void;
  custom: string;
  onCustom: (value: string) => void;
  runView: RunView;
  compare: CompareMode;
  running: string | null;
  timeLimitMs: number;
}) {
  const isCustom = choice === "custom";
  const shown = runView && runView.choice === choice ? runView.report : null;
  return (
    <div data-stdio-io className="shrink-0 border-t border-main bg-main p-3">
      <div role="group" aria-label="入力を選ぶ" className="mb-3 flex flex-wrap gap-2">
        {examples.map((example, index) => (
          <Button
            key={example.label}
            variant={choice === index ? "primary" : "outline"}
            size="md"
            onClick={() => onChoice(index)}
            aria-pressed={choice === index}
            data-stdio-choice={index}
          >
            {example.label}
          </Button>
        ))}
        <Button variant={isCustom ? "primary" : "outline"} size="md" onClick={() => onChoice("custom")} aria-pressed={isCustom} data-stdio-choice="custom">
          自分の入力
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="min-w-0">
          <p className="m-0 mb-1 text-[17px] font-bold text-main">入力</p>
          <Textarea
            aria-label="入力"
            data-stdio-input
            readOnly={!isCustom}
            value={isCustom ? custom : examples[choice as number].input}
            onChange={(event) => onCustom(event.target.value)}
            placeholder="ここに自分の入力を書けます"
            rows={4}
            className={`block min-h-[96px] rounded-lg border border-main bg-surface p-3 font-mono text-[16px] leading-[1.6] ${isCustom ? "" : "text-sub"}`}
          />
        </div>
        <div className="min-w-0">
          <p className="m-0 mb-1 text-[17px] font-bold text-main">出力</p>
          <RunOutput report={shown} running={running} examples={examples} choice={choice} compare={compare} timeLimitMs={timeLimitMs} />
        </div>
      </div>
    </div>
  );
}

function RunOutput({
  report,
  running,
  examples,
  choice,
  compare,
  timeLimitMs,
}: {
  report: RunReport | null;
  running: string | null;
  examples: CodingTestExample[];
  choice: InputChoice;
  compare: CompareMode;
  timeLimitMs: number;
}) {
  const box = "block min-h-[96px] max-h-[220px] overflow-auto rounded-lg border border-main bg-surface p-3 font-mono text-[16px] leading-[1.6] text-main whitespace-pre-wrap break-all";
  if (running) return <p data-stdio-output="running" className={`m-0 ${box} font-sans text-sub`}>{phraseText(running)}</p>;
  if (!report) return <pre data-stdio-output="empty" className={`m-0 ${box} text-sub`}>「実行する」を押すと、ここに出力が出ます</pre>;
  if (report.status === "unavailable") return <p role="status" data-stdio-output="unavailable" className={`m-0 ${box} font-sans`}>{phraseText(report.message)}</p>;
  if (report.status === "timeout") {
    return (
      <p role="status" data-stdio-output="timeout" className={`m-0 ${box} font-sans`}>
        {phraseText(`時間切れです。${timeLimitMs / 1000} 秒を越えたので止めました。終わらない繰り返しが無いか、入力が大きいときの計算の量を見直してください`)}
      </p>
    );
  }
  const expected = choice === "custom" ? null : examples[choice].output;
  const matches = expected !== null && report.exitCode === 0 ? compareOutput(report.stdout, expected, compare) : null;
  const kind = report.outputTooLarge ? "too-much-output" : report.exitCode !== 0 ? "run-error" : matches === false ? "differs" : "ran";
  return (
    <div className="flex flex-col gap-2" data-stdio-output={kind}>
      <pre className={`m-0 ${box}`}>{report.stdout || (report.exitCode === 0 ? "(何も出力されませんでした)" : "")}</pre>
      {report.outputTooLarge ? <p className={`m-0 text-[17px] font-bold text-error ${WRAP}`}>出力が多すぎます。1MB までです</p> : null}
      {report.stderr ? <pre className={`m-0 ${box} min-h-0 text-error`}>{report.stderr}</pre> : null}
      <p className={`m-0 text-[17px] leading-[1.6] ${matches === false || report.exitCode !== 0 ? "font-bold text-error" : "text-sub"} ${WRAP}`}>
        {phraseText(
          report.exitCode !== 0
            ? `実行時エラーで止まりました (終了コード ${report.exitCode})`
            : matches === true
              ? `期待する出力と合っています (${report.ms} ミリ秒)`
              : matches === false
                ? "期待する出力と違います。下の「期待する出力」と見比べてください"
                : `実行しました (${report.ms} ミリ秒)`,
        )}
      </p>
      {matches === false && expected !== null ? (
        <details open className="rounded-lg border border-main bg-surface p-3">
          <summary className="cursor-pointer text-[17px] font-bold text-main">期待する出力</summary>
          <pre className="m-0 mt-2 whitespace-pre-wrap break-all font-mono text-[16px] leading-[1.6] text-main">{expected}</pre>
        </details>
      ) : null}
    </div>
  );
}
